/**
 * Native-shell storage for the WagePro ADMIN session id.
 *
 * Why this exists at all: the production Capacitor shell serves its WebView
 * from `https://app.wagepro.ai` while `apps/api` is served from
 * `https://api.ledgerpro.ai`. Those are different registrable domains, so
 * every `/trpc` call the shell makes is CROSS-SITE, and the production
 * session cookie is `SameSite=Lax` (`apps/api/src/lib/session_cookie.ts`) —
 * the browser accepts the `Set-Cookie` and then never sends it back. The
 * shell therefore asks `wageproAuth.signup`/`login` for the session id in
 * the response body (`client: 'native-shell'`) and replays it as a Bearer
 * header. See `docs/adr/20260908_01_wagepro-native-shell-session-bearer.md`.
 *
 * NATIVE ONLY, and structurally so — not by convention. Every function takes
 * a `native` flag defaulting to `isNative()`, and when it is `false` the
 * function returns `null` / no-ops WITHOUT EVER TOUCHING THE ADAPTER. The
 * default adapter is constructed lazily, inside that guard, so a browser
 * build never even reaches the `@capacitor/preferences` bridge. There is
 * deliberately NO localStorage fallback (unlike
 * `apps/costpro-web/src/lib/preferences_store.ts`, which is a general
 * preferences layer): a real session token in `localStorage` is precisely
 * what `docs/security-rules.md` forbids and `scripts/lint-token-storage.sh`
 * exists to catch. On web the session stays where it belongs — in the
 * httpOnly cookie — and this module is inert.
 *
 * On native, `@capacitor/preferences` writes to `UserDefaults` (iOS) /
 * `SharedPreferences` (Android): OS-sandboxed to this app, but NOT
 * Keychain/Keystore-backed and NOT encrypted at rest. That is the sanctioned
 * -but-weaker guarantee `docs/security-rules.md` documents; genuine
 * Keychain/Keystore storage is tracked separately as issue #24008.
 *
 * The storage adapter is an injectable parameter (same seam as
 * `token_store.ts`) so this module and everything above it is unit-testable
 * in `bun:test` with no Capacitor bridge and no DOM.
 */
import { isNative } from '../platform.js';

/**
 * Minimal async storage surface this module needs. Async (unlike
 * `token_store.ts`'s `TokenStorageAdapter`) because the real implementation
 * is `@capacitor/preferences`, whose API is promise-based.
 */
export interface NativeSessionStorageAdapter {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

/**
 * Storage key for the native-shell session id. Versioned (`.v1`) following
 * `token_store.ts`'s `STORAGE_KEY` convention, so a future change to what is
 * stored here is a one-line key bump that forces one clean re-login rather
 * than a migration.
 */
export const STORAGE_KEY = 'wagepro.native_session.v1';

/** Options accepted by every function in this module. */
export interface NativeSessionOptions {
  /**
   * Whether this is a native Capacitor runtime. Defaults to `isNative()`.
   * Injectable so tests can exercise both branches without a bridge, and so
   * the web branch is provably reachable.
   */
  native?: boolean;
  /** Storage adapter. Defaults to the lazily-built Capacitor Preferences wrapper. */
  adapter?: NativeSessionStorageAdapter;
}

/**
 * Last value read/written, so the common case — a Bearer header resolved on
 * every single tRPC request — does not cross the Capacitor bridge each time.
 * `undefined` means "not yet read"; `null` means "read, and there is none".
 * Invalidated (not updated blindly) by {@link setNativeSessionToken} and
 * {@link clearNativeSessionToken} so the next read re-derives from the store.
 */
let memo: string | null | undefined;

/**
 * Bumped by every write (set / clear / reset). A read captures the current
 * value before crossing the bridge and only installs its result if the
 * counter is unchanged — so a write that lands mid-read cannot be clobbered
 * by the older value the read is still carrying.
 *
 * A counter rather than a re-check of `memo` itself because TypeScript
 * narrows a module-level `let` to `undefined` after the early-return guard
 * below and does NOT widen it again across an `await` (a known unsoundness):
 * `if (memo === undefined)` would be reported by
 * `@typescript-eslint/no-unnecessary-condition` as always-true even though it
 * is genuinely false at runtime in exactly the race this guards.
 */
let memoGeneration = 0;

/**
 * Reads `memo` through a function call so the caller sees its DECLARED type
 * rather than whatever the control-flow analysis narrowed it to. Same reason
 * as {@link memoGeneration}: narrowing across an `await` on module state is
 * unsound here, and reading through this boundary keeps the checks honest.
 */
function readMemo(): string | null | undefined {
  return memo;
}

/** Test seam: drop the memo so a fresh read hits the adapter again. */
export function resetNativeSessionMemo(): void {
  memo = undefined;
  memoGeneration += 1;
}

let defaultAdapter: NativeSessionStorageAdapter | null = null;

/**
 * The real Capacitor Preferences adapter, built on first use.
 *
 * Lazy on purpose: it is only ever reached from inside a `native === true`
 * branch, so a plain browser build never constructs it and never touches the
 * `@capacitor/preferences` web fallback (which is `localStorage`, and would
 * be a token-storage violation).
 */
function getDefaultAdapter(): NativeSessionStorageAdapter {
  defaultAdapter ??= {
    async get(key) {
      const { Preferences } = await import('@capacitor/preferences');
      const { value } = await Preferences.get({ key });
      return value ?? null;
    },
    async set(key, value) {
      const { Preferences } = await import('@capacitor/preferences');
      // TOKEN_STORAGE_OK(#24008): native-only session id; this module has no web branch at all (every entry point returns early when native === false, before the adapter is constructed), so Preferences here is UserDefaults/SharedPreferences, never the localStorage web fallback. Keychain/Keystore parity tracked in the same issue.
      await Preferences.set({ key, value });
    },
    async remove(key) {
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.remove({ key });
    },
  };
  return defaultAdapter;
}

/**
 * The stored native-shell session id, or `null`.
 *
 * Returns `null` immediately on web — the adapter is never consulted, so a
 * browser cannot read (or be made to read) a token out of this store.
 * A stored empty string is treated as absent: an empty Bearer header would
 * authenticate as nobody and produce a confusing 401 rather than a clean
 * "signed out".
 */
export async function getNativeSessionToken(
  opts: NativeSessionOptions = {},
): Promise<string | null> {
  const { native = isNative(), adapter } = opts;
  if (!native) return null;
  const cached = readMemo();
  if (cached !== undefined) return cached;

  const generation = memoGeneration;
  const store = adapter ?? getDefaultAdapter();
  let value: string | null;
  try {
    value = await store.get(STORAGE_KEY);
  } catch {
    // A store read failure is indistinguishable, for our purposes, from "no
    // token": both mean we cannot present a credential. Fail to `null` and
    // let the caller surface the normal signed-out path rather than crashing
    // the shell's boot on a bridge hiccup.
    return null;
  }

  if (generation !== memoGeneration) {
    // A set/clear landed while this read was crossing the bridge. That value
    // is newer than the one we just read, so it wins — installing ours would
    // serve a stale credential for the rest of the process lifetime.
    return readMemo() ?? null;
  }
  memo = value !== null && value !== '' ? value : null;
  return memo;
}

/**
 * Persist the native-shell session id.
 *
 * No-op on web (the httpOnly cookie is the session there). Rejects if the
 * underlying store throws — the caller must know persistence failed, because
 * navigating into the app without a stored token is the 401 boot loop this
 * whole change exists to remove.
 */
export async function setNativeSessionToken(
  token: string,
  opts: NativeSessionOptions = {},
): Promise<void> {
  const { native = isNative(), adapter } = opts;
  if (!native) return;
  const store = adapter ?? getDefaultAdapter();
  await store.set(STORAGE_KEY, token);
  memo = token;
  memoGeneration += 1;
}

/**
 * Drop the stored native-shell session id (sign-out).
 *
 * No-op on web.
 *
 * The memo is set to `null` — NOT `undefined` — before the adapter is
 * touched. `undefined` means "not yet read" and would send the next read
 * back to the store, which still holds the token if the bridge `remove`
 * threw; this process would then resume serving a credential the user has
 * just signed out of. `null` means "there is none", so the current process
 * stops presenting it regardless of whether the removal actually landed. A
 * later launch re-reads the store and, if the removal did fail, presents a
 * token that `auth.logout` has already revoked server-side — which 401s and
 * renders as signed out. Both layers therefore fail safe.
 */
export async function clearNativeSessionToken(
  opts: NativeSessionOptions = {},
): Promise<void> {
  const { native = isNative(), adapter } = opts;
  memo = null;
  memoGeneration += 1;
  if (!native) return;
  const store = adapter ?? getDefaultAdapter();
  await store.remove(STORAGE_KEY);
}
